/**
 * AccessibilityPage — Toegankelijkheid (spec §8.8).
 *
 * The same canonical preference toggles as Meer (shared PreferenceToggles +
 * useUserSettings, so both surfaces always show one truth), here with fuller
 * explanations and a live preview card. Display preferences take effect
 * immediately via <html> data-attributes; the preview reflects them because
 * the hooks are global (text size scales the root font size, motion and
 * contrast hooks override tokens/durations).
 *
 * The OS-level reduced-motion preference is shown as honest information only
 * — the in-app toggle is independent and always user-controlled.
 */
import { Banner, Card, SyncStatus } from '@/components/ui'
import { PageHeader } from '@/components/navigation'
import { PATHS } from '@/app/router/paths'
import { PreferenceToggles } from './PreferenceToggles'
import { useUserSettings } from './useUserSettings'
import './settings.css'

function osPrefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export default function AccessibilityPage() {
  const settings = useUserSettings()
  const osReducedMotion = osPrefersReducedMotion()

  return (
    <div className="settings-page">
      <PageHeader
        title="Toegankelijkheid"
        subtitle="Pas de app aan aan wat voor jou werkt"
        backTo={PATHS.app.more}
      />

      {osReducedMotion && (
        <Banner tone="info">
          Je besturingssysteem geeft aan minder beweging te prefereren. De app volgt die voorkeur
          al; met de schakelaar hieronder kun je animaties ook altijd zelf uitzetten.
        </Banner>
      )}
      {!settings.cloudAvailable && !settings.loading && (
        <Banner tone="info">
          De cloudverbinding is niet beschikbaar. Je voorkeuren worden alleen op dit apparaat
          bewaard.
        </Banner>
      )}

      <section className="settings-section" aria-label="Voorkeuren">
        <h2 className="settings-section__title">Voorkeuren</h2>
        <Card>
          {settings.loading ? (
            <p className="settings-loading">Voorkeuren laden…</p>
          ) : (
            <PreferenceToggles values={settings.values} update={settings.update} variant="full" />
          )}
          <div className="settings-sync-row" aria-live="polite">
            <SyncStatus state={settings.syncState} />
          </div>
          {settings.error && (
            <p className="settings-inline-error" role="alert">
              {settings.error.userMessage}
            </p>
          )}
        </Card>
      </section>

      <section className="settings-section" aria-label="Voorbeeld">
        <h2 className="settings-section__title">Voorbeeld</h2>
        <div className="a11y-preview" aria-live="polite">
          <p className="a11y-preview__title">Zo ziet tekst er nu uit</p>
          <p className="a11y-preview__body">
            Dit voorbeeld volgt direct je gekozen tekstgrootte en contrast. Verminderde beweging
            stop je hier niet, maar animaties in de rest van de app worden direct korter of
            uitgeschakeld.
          </p>
          <p className="a11y-preview__note">
            Tekstgrootte:{' '}
            {settings.values.textSize === 'small'
              ? 'Klein'
              : settings.values.textSize === 'large'
                ? 'Groot'
                : 'Normaal'}{' '}
            · Hoog contrast: {settings.values.highContrast ? 'aan' : 'uit'} · Verminderde beweging:{' '}
            {settings.values.reducedMotion ? 'aan' : 'uit'}
          </p>
        </div>
      </section>
    </div>
  )
}
